import React, {useContext} from "react";
import {Trans, useTranslation} from "react-i18next";
import {Info} from "@material-ui/icons";
import Moment from "react-moment";
import ReactTooltip from 'react-tooltip'
import {useSnackbar} from "notistack";

import AccountDomainElement from "./AccountDomainElement";
import {callAPI} from "../../utils/utils";
import MenuButton from "../../components/MenuButton/MenuButton";

import "./Accounts.scss";
import PopupContext from "../../components/Popup/PopupContext";
import ConfirmDeleteDialog from "./ConfirmDeleteDialog";
import {useGlobalModalContext} from "../../components/GlobalModal/GlobalModal";

const AccountsOverviewElement = (props) => {
  const {setPopupContent} = useContext(PopupContext);
  const {showConfirmDialog, showDialog} = useGlobalModalContext();
  const {enqueueSnackbar} = useSnackbar();
  const {t} = useTranslation();
  const account = props.account;

  /** ACTIONS **/

  const accountActionEdit = () => {
    window.location.replace('https://de.page4.com/customer/autologin/?type=' + (props.account.type === 'free' ? 'light' : 'retail') + '&id=' + props.account.id);
  }

  const accountActionUpgradeShop = () => {
    window.location.replace('https://de.page4.com/customer/shop/?account_id=' + props.account.id)
  }

  const accountActionUpgrade = () => {
    window.location.replace('https://de.page4.com/customer/upgrade/?id=' + props.account.id);
  }

  const accountActionSeo = () => {
    window.location.replace('https://de.page4.com/customer/google/manage/' + props.account.id + "/");
  }

  const accountActionAddDomain = () => {
    showDialog('addDomain', false, {account: account}).then((result) => {
      if (result) {
        enqueueSnackbar(t('Accounts.Snackbars.DomainAdded'), {
          variant: 'success',
        });
        window.refreshAccounts();
      }
    });
  }

  const accountActionActivate = () => {
    showDialog('activateAccount').then((result) => {
      if (result) {
        callAPI("account", "POST", "upgrade", {account_id: props.account.id, package_id: result}).then(() => {
          enqueueSnackbar(t('Accounts.Snackbars.AccountActivated'), {
            variant: 'success',
          });
          window.refreshAccounts();
        });
      }
    });
  }

  const accountActionCancel = () => {
    const infoText = props.account.cancelled ? "Soll die Kündigung der Webseite rückgängig gemacht werden?" : "Soll die Webseite zum Laufzeitende gekündigt werden?";
    const title = props.account.cancelled ? "Kündigung zurücknehmen" : "Webseite kündigen";
    const buttonText = props.account.cancelled ? "Kündigung zurücknehmen" : "Kündigen";

    showConfirmDialog(title, infoText, {confirmButton: buttonText})
      .then((result) => {
        if (result) {
          callAPI("account", "POST", "cancel", {
            account_id: props.account.id,
            state: !props.account.cancelled
          }).then(() => {
            enqueueSnackbar(props.account.cancelled ? "Die Kündigung der Webseite wurde rückgängig gemacht" : "Die Webseite wurde zum Laufzeitende gekündigt", {
              variant: 'success',
            });
            window.refreshAccounts();
          });
        }
      });
  }

  const accountActionDelete = () => {
    setPopupContent(<ConfirmDeleteDialog account={account} onClose={(result) => {
      if (result) {
        callAPI("account", "POST", "cancel", {
          account_id: props.account.id,
          state: true,
          is_free: account.type === 'free'
        }).then(() => {
          enqueueSnackbar(t('Accounts.Snackbars.AccountDeleted'), {variant: 'success'});
          window.refreshAccounts();
        });
      }
    }}/>);
  }

  /** PREPARE MENU ITEMS **/

  let menuItems = [
    {caption: 'Webseite bearbeiten', action: accountActionEdit, enabled: account.online},
  ];

  if (account.type !== "contributor") {
    if (account.type === "free") {
      menuItems.push({caption: 'Webseite auf Vollversion upgraden', action: accountActionUpgrade, enabled: true});
    }

    if (account.shop_enabled && account.shop_demo)
      menuItems.push({caption: 'Shop auf Vollversion upgraden', action: accountActionUpgradeShop, enabled: true});

    if (account.type === "retail")
      menuItems.push({caption: 'Domain hinzufügen', action: accountActionAddDomain, enabled: account.online});

    if (account.default_domain && account.type === "retail")
      menuItems.push({caption: 'Webseite bei Google anmelden', action: accountActionSeo, enabled: true});

    if (account.type === "agency" || account.type === "free")
      menuItems.push({caption: 'Webseite löschen', action: accountActionDelete, enabled: true});

    if (account.type === "retail")
      menuItems.push({
        caption: account.cancelled ? 'Kündigung zurücknehmen' : 'Webseite kündigen',
        action: accountActionCancel,
        enabled: true
      });

    if (account.type === "agency")
      menuItems.push({caption: 'Webseite aktivieren', action: accountActionActivate, enabled: true});
  }

  /** DOMAINS **/

  const domainList = account.domains.map(domain => {
    return <AccountDomainElement key={domain.id} disableMenu={account.type === 'contributor'} domain={domain}/>;
  });

  return (
    <>
      <div className={'element'}>
        <div className={'basic'}>
          <div className={'id'}>
            <div className={'col-title'}>{account.type === 'free' ?
              <>{account.description}<a className={'edit-link'} href={'https://de.page4.com/customer/autologin/?type=light&id=' + account.id}>Bearbeitung öffnen</a></>
              :
              <>#{("00000" + account.id).slice(-5)}<a className={'edit-link'} href={'https://de.page4.com/customer/autologin/?type=retail&id=' + account.id}>Bearbeitung öffnen</a></>
            }</div>
            <div style={{fontSize: 14}}>{account.package}</div>
          </div>
          <div className={'end-date'}>
            <div className={'col-title'}>Laufzeit</div>
            {account.type === 'retail' ? <Moment format="DD.MM.YYYY" unix>{account.end_date}</Moment> : <time>Unbegrenzt</time>}&nbsp;
            {account.cancelled && <Info
              style={{width: 16, height: 16, position: 'absolute', top: -3, color: 'rgba(255, 75, 75, 1)'}}
              data-tip={'Dieser Account wurde zum Laufzeitende gekündigt'}/>
            }

          </div>
          <div className={'col-status ' + (account.online  ? 'online' : 'offline')}>
            <div className={'col-title'}>
              Status
            </div>
            <span>
              {account.online ? <Trans>Accounts.Element.StatusOnline</Trans> :
                <Trans>Accounts.Element.StatusOffline</Trans>}
            </span>
          </div>
          <div className={'col-domain'}>
            <div className={'col-title'}>
              Hauptdomain
            </div>
            <span>
              {account.default_domain ? <a target="_blank" rel="noreferrer" href={'https://' + (account.type !== "free" ? "www." : "") + account.default_domain}>{account.default_domain}</a> : <span><Trans>Accounts.Element.NoDefaultDomain</Trans></span>}
            </span>
            {account.future_default && <>
              <div style={{display:'inline-block'}}>
                <span style={{height:25, textAlign: 'center'}}><svg xmlns="http://www.w3.org/2000/svg" height="24px" viewBox="0 0 24 24" width="24px" fill="#000000"><path d="M24 24H0V0h24v24z" fill="none" opacity=".87"/><path d="M16.59 8.59L12 13.17 7.41 8.59 6 10l6 6 6-6-1.41-1.41z"/></svg></span>
                <span style={{position: 'relative', color: 'rgba(0, 0, 0, 0.7)', textAlign: 'center'}}>
                  {account.future_default}
                  {/*<Info*/}
                  {/*  style={{width: 16, height: 16, position: 'absolute', top: -3, color: 'rgba(255, 75, 75, 1)'}}*/}
                  {/*  data-tip={'Diese Domain wird die zukünftige Hauptdomain'}/>*/}
                </span>
              </div>
            </>
            }
          </div>
          <div style={{position: 'relative', flexGrow: 1}}>
            <MenuButton items={menuItems}/>
          </div>
        </div>
        {account.shop_enabled &&
          <div className={'row-shop'}>
            <div className={'col-package'}>
              Shopsystem
            </div>
            <div className={'col-end-date'}>
              {account.shop_demo ? <time>Unbegrenzt</time> : <Moment format="DD.MM.YYYY" unix>{account.end_date}</Moment>}
            </div>
            <div className={'col-status'}>
              {account.shop_demo ? <span>Testversion</span> : <span>Vollversion</span>}
            </div>
          </div>
        }

        <div className={'details'}>
          {account.domains.length > 0 &&
            <div className={'account-domain-list'}>
              {domainList}
            </div>
          }

          {/*{JSON.stringify(account.domains)}*/}
        </div>
      </div>
      < ReactTooltip/>
    </>
  );
}

export default AccountsOverviewElement;